import { type ReactElement, type ReactNode, isValidElement } from 'react';

import { MarkdownProvider, isDefined } from 'common';

import { MarkdownCodeMirror, type MarkdownCodeMirrorProps } from './CodeMirror';

interface StaffMarkdownProviderProps {
  children: ReactNode;
}
export function StaffMarkdownProvider(props: StaffMarkdownProviderProps) {
  const { children } = props;
  return (
    <MarkdownProvider
      extraComponents={{
        /**
         * All code blocks are wrapped in `pre` elements.
         * There are two types of code blocks:
         * 1. Inline code blocks (e.g. `{ x: 420 }`)
         * 2. Complex code blocks (e.g. JSON, JavaScript, etc.)
         * e.g.
         * ~~~json
         * { x: 420 }
         * ~~~
         *
         * This section handles the two types of code blocks.
         * For inline code blocks, do not use `MarkdownCodeMirror`. Use the `pre` element with children.
         * For complex code blocks, use the `MarkdownCodeMirror` component.
         */
        pre: (props) => {
          const { children } = props;
          /**
           * The `children` prop is a `ReactNode`, which could be an element, an array, a string, null, or undefined.
           * If it is an element, we can pull it out and use it for <MarkdownCodeMirror />.
           */
          let codeChild: ReactElement<MarkdownCodeMirrorProps> | undefined;
          if (isValidElement<MarkdownCodeMirrorProps>(children) && children.type === 'code') {
            codeChild = children;
          }
          return (
            <pre>
              {isDefined(codeChild) ? <MarkdownCodeMirror {...codeChild.props} /> : children}
            </pre>
          );
        },
      }}
    >
      {children}
    </MarkdownProvider>
  );
}
